import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { format, parseISO } from "date-fns";
import { Row } from "../modules/storage/models/storage-dto";
import { useStorageContext } from "../contexts/storage/storage-provider";
import { parseMonetaryInputWithPrecision } from "../utils";
import { useStorageEndpoint } from "./useStorageEndpoint";
import { InitialValues } from "../pages/Storage/models/types";
import { buildInitialValues } from "../pages/Storage/utils";

interface Params {
  data: Row;
  open: boolean;
  onClose: VoidFunction;
  serviceContainer: any;
}

export const useEditStorage = ({ data, open }: Params) => {
  const endpoint = useStorageEndpoint();
  const isDischarge = endpoint === "unload";

  const initialValues: InitialValues = useMemo(
    () => buildInitialValues(data, isDischarge),
    [data, isDischarge]
  );

  const formik = useFormik({
    initialValues,
    onSubmit: () => {},
    enableReinitialize: true,
  });

  const { nfs, fetchNfsByContract } = useStorageContext();

  const [loading, setLoading] = useState({
    commit: false,
    reject: false,
    getNfs: false,
  });

  const scheduleDateValue = useMemo(
    () => parseISO(formik.values.scheduleDate),
    [formik.values.scheduleDate]
  );

  const orderDateValue = useMemo(
    () => parseISO(formik.values.orderDate),
    [formik.values.orderDate]
  );

  const setScheduleDate = (value: Date | null) => {
    if (!value) return;
    const dateTimeReference = format(value, "yyyy-MM-dd");
    formik.setFieldValue("scheduleDate", dateTimeReference);
  };

  const setOrderDate = (value: Date | null) => {
    if (!value) return;
    const dateTimeReference = format(value, "yyyy-MM-dd");
    formik.setFieldValue("orderDate", dateTimeReference);
  };

  const setVolumeRequested = (value: string) => {
    const cleanValue = value.replace(/\./g, "");
    formik.setFieldValue("volumeRequested", cleanValue);
  };

  const setUnitPrice = (value: string) => {
    const cleanValue = parseMonetaryInputWithPrecision(value, 6);
    formik.setFieldValue("unitPrice", cleanValue);
  };

  const getNfsByContract = useCallback(
    async (contractId: string) => {
      setLoading((prev) => ({ ...prev, getNfs: true }));

      await fetchNfsByContract(contractId);

      setLoading((prev) => ({ ...prev, getNfs: false }));
    },
    [fetchNfsByContract]
  );

  useEffect(() => {
    if (open && data.contract.id) {
      getNfsByContract(data.contract.id);
    }
  }, [open, data.contract.id]);

  return {
    customerName: data.contract?.customerCompany?.description,
    productName: data.contract?.product?.description,
    status: data.status,

    formik,
    scheduleDateValue,
    nfs,
    orderDateValue,
    setScheduleDate,
    setOrderDate,
    setVolumeRequested,
    setUnitPrice,

    getNfsByContract,
    loading,
    isDischarge,
  };
};
